import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";
import { Plus, Trash2, Save, Settings, Calculator } from "lucide-react";

export default function DealerConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    const r = await api.get("/dealership/config");
    setCfg(r.data); setDirty(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.patch("/dealership/config", cfg);
      toast.success("Config saved");
      setDirty(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const update = (patch) => { setCfg({ ...cfg, ...patch }); setDirty(true); };

  if (!cfg) return <div className="text-muted-foreground p-6">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="dealer-config">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1"><Settings className="w-3 h-3" /> Business rules</div>
          <div className="text-sm text-muted-foreground mt-1">Configure statutory & policy parameters used by pricing, discount, and booking flows.</div>
        </div>
        <Button onClick={save} disabled={!dirty} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="config-save">
          <Save className="w-3.5 h-3.5" /> Save changes
        </Button>
      </div>

      {/* RTO slabs */}
      <div className="card-sharp p-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">RTO slabs (state-wise road tax)</div>
        <div className="space-y-2">
          {cfg.rto_slabs.map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`rto-slab-${i}`}>
              <Label className="col-span-2 text-xs">Up to ₹</Label>
              <Input type="number" value={s.upto ?? ""} placeholder="∞" onChange={(e) => {
                const v = e.target.value ? Number(e.target.value) : null;
                update({ rto_slabs: cfg.rto_slabs.map((x, j) => j === i ? { ...x, upto: v } : x) });
              }} className="col-span-4 rounded-[2px] font-mono" />
              <Label className="col-span-2 text-xs">Rate %</Label>
              <Input type="number" step="0.1" value={s.rate_pct} onChange={(e) => update({
                rto_slabs: cfg.rto_slabs.map((x, j) => j === i ? { ...x, rate_pct: Number(e.target.value) } : x),
              })} className="col-span-3 rounded-[2px] font-mono" />
              <button className="col-span-1 text-muted-foreground hover:text-red-500" onClick={() => update({ rto_slabs: cfg.rto_slabs.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <Button variant="ghost" size="sm" className="mt-2 gap-1" onClick={() => update({ rto_slabs: [...cfg.rto_slabs.slice(0, -1), { upto: (cfg.rto_slabs.at(-2)?.upto || 0) + 500000, rate_pct: 0 }, cfg.rto_slabs.at(-1)] })} data-testid="add-rto-slab"><Plus className="w-3.5 h-3.5" /> Add slab</Button>
        <div className="text-[10px] text-muted-foreground mt-2 italic">Leave the last "Up to" blank to mean "and above". Slabs are auto-sorted on save.</div>
      </div>

      {/* TCS + Booking + Cash */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-sharp p-4">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">TCS (206C(1F))</div>
          <Label className="text-xs">Threshold ₹</Label>
          <Input type="number" value={cfg.tcs?.threshold || 0} onChange={(e) => update({ tcs: { ...cfg.tcs, threshold: Number(e.target.value) } })} className="rounded-[2px] mt-1 font-mono" data-testid="tcs-threshold" />
          <Label className="text-xs mt-2 block">Rate %</Label>
          <Input type="number" step="0.1" value={cfg.tcs?.rate_pct || 0} onChange={(e) => update({ tcs: { ...cfg.tcs, rate_pct: Number(e.target.value) } })} className="rounded-[2px] mt-1 font-mono" data-testid="tcs-rate" />
        </div>
        <div className="card-sharp p-4">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Booking amount formula</div>
          <Label className="text-xs">% of Ex-Showroom</Label>
          <Input type="number" step="0.1" value={cfg.booking_amount?.pct_of_exshowroom || 0} onChange={(e) => update({ booking_amount: { ...cfg.booking_amount, pct_of_exshowroom: Number(e.target.value) } })} className="rounded-[2px] mt-1 font-mono" data-testid="ba-pct" />
          <Label className="text-xs mt-2 block">Round-up step ₹</Label>
          <Input type="number" value={cfg.booking_amount?.round_up_step || 0} onChange={(e) => update({ booking_amount: { ...cfg.booking_amount, round_up_step: Number(e.target.value) } })} className="rounded-[2px] mt-1 font-mono" data-testid="ba-step" />
          {/* Iter-150 · Live preview — ops sees the impact of the two knobs above the moment they tweak them. */}
          <BookingAmountPreview cfg={cfg} />
        </div>
        <div className="card-sharp p-4">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Cash & validity</div>
          <Label className="text-xs">Cash limit ₹ (RBI 269ST)</Label>
          <Input type="number" value={cfg.cash_limit_inr || 0} onChange={(e) => update({ cash_limit_inr: Number(e.target.value) })} className="rounded-[2px] mt-1 font-mono" data-testid="cash-limit" />
          <Label className="text-xs mt-2 block">Quotation validity (days)</Label>
          <Input type="number" value={cfg.quotation_validity_days || 30} onChange={(e) => update({ quotation_validity_days: Number(e.target.value) })} className="rounded-[2px] mt-1 font-mono" />
        </div>
      </div>

      {/* Discount categories */}
      <div className="card-sharp p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Discount categories</div>
            <div className="text-xs text-muted-foreground mt-1">Combined cap: <span className="font-mono">{cfg.max_combined_discount_pct}%</span></div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Combined cap %</Label>
            <Input type="number" step="0.1" value={cfg.max_combined_discount_pct} onChange={(e) => update({ max_combined_discount_pct: Number(e.target.value) })} className="w-24 rounded-[2px] font-mono" data-testid="cap-pct" />
          </div>
        </div>
        <table className="w-full dense">
          <thead><tr><th>Key</th><th>Label</th><th>Max %</th><th>Required Doc</th><th>Approver</th><th></th></tr></thead>
          <tbody>
            {cfg.discount_categories.map((d, i) => (
              <tr key={i} data-testid={`disc-cat-${d.key}`}>
                <td><Input value={d.key} onChange={(e) => update({ discount_categories: cfg.discount_categories.map((x, j) => j === i ? { ...x, key: e.target.value } : x) })} className="rounded-[2px] h-7 text-xs font-mono" /></td>
                <td><Input value={d.label} onChange={(e) => update({ discount_categories: cfg.discount_categories.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })} className="rounded-[2px] h-7 text-xs" /></td>
                <td><Input type="number" step="0.1" value={d.max_pct} onChange={(e) => update({ discount_categories: cfg.discount_categories.map((x, j) => j === i ? { ...x, max_pct: Number(e.target.value) } : x) })} className="rounded-[2px] h-7 text-xs text-right font-mono" /></td>
                <td><Input value={d.required_doc || ""} onChange={(e) => update({ discount_categories: cfg.discount_categories.map((x, j) => j === i ? { ...x, required_doc: e.target.value } : x) })} className="rounded-[2px] h-7 text-xs" /></td>
                <td><Input value={d.approver_role || ""} onChange={(e) => update({ discount_categories: cfg.discount_categories.map((x, j) => j === i ? { ...x, approver_role: e.target.value } : x) })} className="rounded-[2px] h-7 text-xs font-mono" /></td>
                <td><button onClick={() => update({ discount_categories: cfg.discount_categories.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button variant="ghost" size="sm" className="mt-2 gap-1" onClick={() => update({ discount_categories: [...cfg.discount_categories, { key: `custom_${cfg.discount_categories.length + 1}`, label: "Custom", max_pct: 2.5, required_doc: "", approver_role: "sales_manager" }] })} data-testid="add-disc-cat">
          <Plus className="w-3.5 h-3.5" /> Add category
        </Button>
      </div>
      {/* Combined cap: display + input */}
      <div className="card-sharp p-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Phase-1 lead-capture policy</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex items-start gap-2 cursor-pointer p-2 border rounded-sm hover:bg-muted/20">
            <input type="checkbox" className="mt-1" checked={!!cfg.lead_mandatory_mobile}
              onChange={(e) => update({ lead_mandatory_mobile: e.target.checked })}
              data-testid="cfg-lead-mandatory-mobile" />
            <div>
              <div className="text-sm font-medium">Mobile mandatory on Lead form</div>
              <div className="text-[10px] text-muted-foreground">Frontend + backend both enforce it.</div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer p-2 border rounded-sm hover:bg-muted/20">
            <input type="checkbox" className="mt-1" checked={!!cfg.lead_mandatory_email}
              onChange={(e) => update({ lead_mandatory_email: e.target.checked })}
              data-testid="cfg-lead-mandatory-email" />
            <div>
              <div className="text-sm font-medium">Email mandatory on Lead form</div>
              <div className="text-[10px] text-muted-foreground">Off = capture leads even without email addresses.</div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer p-2 border rounded-sm hover:bg-muted/20">
            <input type="checkbox" className="mt-1" checked={cfg.dl_name_verify_strict !== false}
              onChange={(e) => update({ dl_name_verify_strict: e.target.checked })}
              data-testid="cfg-dl-strict" />
            <div>
              <div className="text-sm font-medium">Strict DL name verification</div>
              <div className="text-[10px] text-muted-foreground">Every substantive lead-name token must appear on the DL.</div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer p-2 border rounded-sm hover:bg-muted/20">
            <input type="checkbox" className="mt-1" checked={cfg.dl_verify_allow_admin_override !== false}
              onChange={(e) => update({ dl_verify_allow_admin_override: e.target.checked })}
              data-testid="cfg-dl-override" />
            <div>
              <div className="text-sm font-medium">Allow admin DL-mismatch override</div>
              <div className="text-[10px] text-muted-foreground">Users can bypass a failed match with a logged reason.</div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer p-2 border rounded-sm hover:bg-muted/20 md:col-span-2">
            <input type="checkbox" className="mt-1" checked={cfg.auto_assign_branch_from_user !== false}
              onChange={(e) => update({ auto_assign_branch_from_user: e.target.checked })}
              data-testid="cfg-auto-branch" />
            <div>
              <div className="text-sm font-medium">Auto-assign branch from user hierarchy on lead create</div>
              <div className="text-[10px] text-muted-foreground">Falls back to the first org branch when the user has no primary branch set.</div>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}

// Iter-150 · Live preview widget for the Booking-amount formula. Mirrors the
// backend logic in `dealer_config_service.compute_booking_amount`:
//   raw    = ex_showroom * pct / 100
//   result = ceil(raw / step) * step
// Ships four common price bands so ops immediately sees the impact of a
// tweak; plus a free-form field for arbitrary "what-if" prices.
function BookingAmountPreview({ cfg }) {
  const [custom, setCustom] = React.useState(1000000);
  const pct = Number(cfg?.booking_amount?.pct_of_exshowroom || 0);
  const step = Number(cfg?.booking_amount?.round_up_step || 0);

  const compute = (ex) => {
    const raw = (ex * pct) / 100;
    if (step > 0) return Math.ceil(raw / step) * step;
    return Math.round(raw * 100) / 100;
  };

  const bands = [
    { label: "₹5 L (hatchback)",  ex: 500000 },
    { label: "₹10 L (sedan)",     ex: 1000000 },
    { label: "₹18 L (SUV)",       ex: 1800000 },
    { label: "₹50 L (luxury)",    ex: 5000000 },
  ];

  return (
    <div className="mt-4 pt-3 border-t border-dashed" data-testid="ba-preview">
      <div className="flex items-center gap-1.5 mb-2">
        <Calculator className="w-3 h-3 text-accent" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-accent">Live preview</span>
      </div>
      <div className="space-y-1 mb-3">
        {bands.map((b) => (
          <div key={b.ex} className="flex justify-between items-center text-[11px]" data-testid={`ba-preview-band-${b.ex}`}>
            <span className="text-muted-foreground">{b.label}</span>
            <span className="font-mono tabular-nums font-medium">
              {fmtINR(compute(b.ex))}
            </span>
          </div>
        ))}
      </div>
      {/* What-if row */}
      <div className="flex items-center gap-2 pt-2 border-t border-dashed">
        <Input
          type="number" value={custom}
          onChange={(e) => setCustom(Number(e.target.value) || 0)}
          className="rounded-[2px] h-7 text-[11px] font-mono flex-1"
          placeholder="Ex-showroom ₹"
          data-testid="ba-preview-custom-input"
        />
        <ArrowRightSlim />
        <div className="font-mono tabular-nums font-medium text-[11px] w-24 text-right" data-testid="ba-preview-custom-out">
          {fmtINR(compute(custom))}
        </div>
      </div>
      <div className="text-[9px] text-muted-foreground mt-2 italic">
        Formula: <span className="font-mono">ceil((ex × {pct}%) ÷ {step || 1}) × {step || 1}</span>
      </div>
    </div>
  );
}

function ArrowRightSlim() {
  return <span className="text-muted-foreground text-xs">→</span>;
}
